import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useMe } from "@/api/hooks/auth";
import { useInviteMember, useMembers, useOrgs, useRemoveMember, type MemberOut } from "@/api/hooks/orgs";
import { errorMessage, fieldError } from "@/api/errors";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useParams } from "react-router-dom";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { PageHeader } from "@/components/PageHeader";

const schema = z.object({
  name: z.string().trim().min(1, "members.nameRequired"),
  email: z.string().trim().min(1, "members.emailRequired").email("members.emailInvalid"),
  role: z.enum(["owner", "member"]),
});
type Values = z.infer<typeof schema>;

export function MembersPage() {
  const { t } = useTranslation();
  const { org = "" } = useParams();
  const me = useMe();
  const orgs = useOrgs();
  const orgName = orgs.data?.find((o) => o.slug === org)?.name ?? org;
  usePageCrumbs([{ label: orgName, to: `/${org}` }, { label: t("members.title") }]);
  const members = useMembers(org);
  const invite = useInviteMember(org);
  const remove = useRemoveMember(org);
  const [toRemove, setToRemove] = useState<MemberOut | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: "", email: "", role: "member" } });
  const { errors } = form.formState;
  const serverName = fieldError(invite.error, "name");
  const serverEmail = fieldError(invite.error, "email");

  const onInvite = form.handleSubmit((v) =>
    invite.mutate(v, {
      onSuccess: () => {
        toast.success(t("members.inviteSent", { email: v.email }));
        form.reset({ name: "", email: "", role: "member" });
      },
    }),
  );

  return (
    <div>
      <PageHeader title={t("members.title")} description={t("members.intro", { org: orgName })} />
      <div className="grid gap-6 lg:grid-cols-main">
        <section aria-label={t("members.title")} className="flex flex-col gap-4">
          {members.isPending ? <PageLoading /> : null}
          {members.isError ? <ErrorNotice error={members.error} /> : null}
          {members.data ? (
            <Table>
              <THead>
                <TR>
                  <TH>{t("members.colName")}</TH>
                  <TH>{t("members.colEmail")}</TH>
                  <TH>{t("members.colRole")}</TH>
                  <TH>
                    <span className="sr-only">{t("members.colActions")}</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {members.data.map((m) => (
                  <TR key={m.user_id}>
                    <TD className="font-medium">
                      <span className="flex flex-wrap items-center gap-2">
                        {m.name}
                        {m.user_id === me.data?.id ? <Badge variant="grey">{t("members.you")}</Badge> : null}
                        {!m.is_active ? <Badge variant="outline">{t("members.inactive")}</Badge> : null}
                      </span>
                    </TD>
                    <TD className="break-all text-fg-muted">{m.email}</TD>
                    <TD>{t(`members.role.${m.role}`)}</TD>
                    <TD className="text-right">
                      <button
                        type="button"
                        onClick={() => setToRemove(m)}
                        aria-label={t("members.remove", { name: m.name })}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full text-heading hover:bg-secondary-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                      >
                        <Trash2 aria-hidden="true" className="h-5 w-5" />
                      </button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : null}
        </section>

        <Card className="self-start">
          <CardHeader>
            <h2 className="text-xl">{t("members.inviteTitle")}</h2>
            <p className="text-sm text-fg-muted">{t("members.inviteIntro")}</p>
          </CardHeader>
          <CardContent>
            <form noValidate onSubmit={onInvite} className="flex flex-col gap-4">
              <FormError message={invite.isError && !serverName && !serverEmail ? errorMessage(invite.error, t("errors.generic")) : null} />
              <Field label={t("members.name")} error={errors.name?.message ? t(errors.name.message) : serverName}>
                {(p) => <Input {...p} autoComplete="off" {...form.register("name")} />}
              </Field>
              <Field label={t("members.email")} error={errors.email?.message ? t(errors.email.message) : serverEmail}>
                {(p) => <Input {...p} type="email" autoComplete="off" {...form.register("email")} />}
              </Field>
              <Field label={t("members.roleLabel")} hint={t("members.roleHelp")}>
                {(p) => (
                  <Select {...p} className="w-full" {...form.register("role")}>
                    <option value="member">{t("members.role.member")}</option>
                    <option value="owner">{t("members.role.owner")}</option>
                  </Select>
                )}
              </Field>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? t("members.sending") : t("members.sendInvite")}
              </Button>
            </form>
          </CardContent>
        </Card>

        <ConfirmDialog
          open={toRemove !== null}
          onOpenChange={(o) => !o && setToRemove(null)}
          title={t("members.removeConfirm", { name: toRemove?.name ?? "", org: orgName })}
          description={t("members.removeBody")}
          confirmLabel={t("members.removeAction")}
          busy={remove.isPending}
          onConfirm={() => {
            if (!toRemove) return;
            const name = toRemove.name;
            remove.mutate(toRemove.user_id, {
              onSuccess: () => toast.success(t("members.removed", { name })),
              onError: (e) => toast.error(errorMessage(e, t("errors.generic"))),
              onSettled: () => setToRemove(null),
            });
          }}
        />
      </div>
    </div>
  );
}
